import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { ROLES } from "../../data/content";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.5 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="line-mask">
    <motion.span
      custom={i}
      variants={lineVariant}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-ink">
      {/* Portrait cutout (right) on dark stage */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-[58%] overflow-hidden">
        {/* ambient gold stage glow behind her */}
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(55% 55% at 62% 42%, rgba(212,175,55,0.18), transparent 70%)" }}
        />
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform flex items-end justify-center lg:justify-end">
          <img
            src="/teresa-cutout.png"
            alt="Teresa W. Lindsey"
            className="h-[96%] lg:h-[104%] w-auto object-contain object-bottom translate-x-2 lg:translate-x-10 drop-shadow-[0_25px_60px_rgba(0,0,0,0.6)]"
          />
        </motion.div>
        {/* subtle base shadow to ground her */}
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-ink to-transparent" />
        <div className="absolute inset-0 lg:hidden bg-ink/55" />
      </div>
      <div className="absolute inset-0 radial-vignette pointer-events-none" />

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 max-w-[88rem] mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mb-7"
        >
          <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.4em] text-gold/90">
            <span className="w-10 h-px bg-gold/50" />
            Ms Chancellor Entertainment
          </span>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.92] tracking-tighter text-white text-5xl sm:text-7xl lg:text-[6.5rem]">
          <Line i={0}>Teresa W.</Line>
          <Line i={1} className="text-gold-gradient italic">Lindsey</Line>
        </h1>

        <div className="mt-8 max-w-xl">
          <span className="line-mask">
            <motion.p
              custom={2}
              variants={lineVariant}
              initial="hidden"
              animate="visible"
              className="font-serif italic text-xl sm:text-2xl text-white/85"
            >
              "If it's in your mind, it's in your future."
            </motion.p>
          </span>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-6 max-w-md text-white/60 text-base leading-relaxed"
        >
          Psalmist, playwright, film director, and visionary entrepreneur — using
          every gift for His glory through ministry, entertainment, and inspiration.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-book-cta"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light transition-colors duration-500"
          >
            Book Teresa
          </a>
          <a
            href="#about"
            data-testid="hero-story-cta"
            className="inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white/85 text-xs uppercase tracking-[0.2em] rounded-sm hover:border-gold hover:text-gold transition-all duration-500"
          >
            Her Story
          </a>
        </motion.div>
      </motion.div>

      {/* roles ticker */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-black/30 backdrop-blur-sm"
      >
        <div className="max-w-[88rem] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-6">
          <div className="hidden sm:flex flex-wrap gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.25em] text-white/50">
            {ROLES.slice(0, 5).map((r) => (
              <span key={r}>{r}</span>
            ))}
          </div>
          <div className="flex items-center gap-2 text-gold/80 text-[11px] uppercase tracking-[0.25em] ml-auto">
            <ArrowDown size={14} className="animate-bounce" /> Scroll
          </div>
        </div>
      </motion.div>
    </section>
  );
};
